import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Brain, ArrowRight, Share2, Download, Check, ChevronRight } from 'lucide-react';
import ReferralPrompt from '../components/ReferralPrompt';

// Types
type BiasType = 'confirmation' | 'anchoring' | 'availability' | 'dunningKruger' | 'sunkCost' | 'authority' | 'bandwagon' | 'optimism';

interface AnswerOption {
  text: string;
  scores: Partial<Record<BiasType, number>>;
}

interface Question {
  id: number;
  text: string;
  options: AnswerOption[];
}

// Bias metadata
const BIAS_INFO: Record<BiasType, { name: string; description: string; color: string }> = {
  confirmation: {
    name: 'Confirmation Bias',
    description: 'You tend to seek information that confirms what you already believe.',
    color: 'bg-blue-500'
  },
  anchoring: {
    name: 'Anchoring Bias',
    description: 'You rely heavily on the first piece of information you encounter.',
    color: 'bg-purple-500'
  },
  availability: {
    name: 'Availability Heuristic',
    description: 'You judge likelihood based on how easily examples come to mind.',
    color: 'bg-green-500'
  },
  dunningKruger: {
    name: 'Dunning-Kruger Effect',
    description: 'You may overestimate your abilities in areas where you have limited knowledge.',
    color: 'bg-orange-500'
  },
  sunkCost: {
    name: 'Sunk Cost Fallacy',
    description: 'You continue investing in something because of what you\'ve already put in.',
    color: 'bg-red-500'
  },
  authority: {
    name: 'Authority Bias',
    description: 'You tend to trust expert opinions without questioning them.',
    color: 'bg-cyan-500'
  },
  bandwagon: {
    name: 'Bandwagon Effect',
    description: 'You\'re influenced by what others are doing or thinking.',
    color: 'bg-pink-500'
  },
  optimism: {
    name: 'Optimism Bias',
    description: 'You tend to underestimate risks and overestimate positive outcomes.',
    color: 'bg-yellow-500'
  }
};

// Type name matrix
const TYPE_MATRIX: Record<string, Record<string, string>> = {
  confirmation: {
    authority: 'The True Believer',
    bandwagon: 'The Echo Chamber',
    optimism: 'The Hopeful Validator',
    anchoring: 'The First Impression Keeper',
    availability: 'The Pattern Matcher',
    dunningKruger: 'The Self-Assured',
    sunkCost: 'The Committed Believer'
  },
  dunningKruger: {
    optimism: 'The Confident Optimist',
    authority: 'The Trusting Novice',
    confirmation: 'The Self-Assured',
    bandwagon: 'The Confident Follower',
    anchoring: 'The Quick Judger',
    availability: 'The Intuitive Guesser',
    sunkCost: 'The Persistent Learner'
  },
  anchoring: {
    sunkCost: 'The Committed Starter',
    confirmation: 'The First Impression Keeper',
    authority: 'The Expert Anchorer',
    optimism: 'The Hopeful Starter',
    bandwagon: 'The Trend Setter',
    availability: 'The Recent Anchorer',
    dunningKruger: 'The Quick Judger'
  },
  availability: {
    optimism: 'The Recent Memory Optimist',
    bandwagon: 'The Trend Follower',
    confirmation: 'The Pattern Matcher',
    authority: 'The News Follower',
    anchoring: 'The Recent Anchorer',
    dunningKruger: 'The Intuitive Guesser',
    sunkCost: 'The Memory Keeper'
  },
  authority: {
    bandwagon: 'The Consensus Seeker',
    optimism: 'The Hopeful Follower',
    confirmation: 'The Loyal Student',
    anchoring: 'The Expert Anchorer',
    availability: 'The News Follower',
    dunningKruger: 'The Trusting Novice',
    sunkCost: 'The Loyal Investor'
  },
  sunkCost: {
    optimism: 'The Hopeful Investor',
    confirmation: 'The Committed Believer',
    bandwagon: 'The Loyal Follower',
    anchoring: 'The Committed Starter',
    authority: 'The Loyal Investor',
    availability: 'The Memory Keeper',
    dunningKruger: 'The Persistent Learner'
  },
  bandwagon: {
    optimism: 'The Positive Conformist',
    confirmation: 'The Social Validator',
    authority: 'The Consensus Seeker',
    anchoring: 'The Trend Setter',
    availability: 'The Trend Follower',
    dunningKruger: 'The Confident Follower',
    sunkCost: 'The Loyal Follower'
  },
  optimism: {
    confirmation: 'The Bright-Side Seeker',
    authority: 'The Hopeful Follower',
    bandwagon: 'The Positive Conformist',
    anchoring: 'The Hopeful Starter',
    availability: 'The Recent Memory Optimist',
    dunningKruger: 'The Confident Optimist',
    sunkCost: 'The Hopeful Investor'
  }
};

// Questions
const QUESTIONS: Question[] = [
  // Confirmation Bias (Q1-Q3)
  {
    id: 1,
    text: "You read an article that contradicts something you believe strongly. You...",
    options: [
      { text: "Look for other articles that support your original view", scores: { confirmation: 3 } },
      { text: "Consider the new evidence carefully", scores: {} },
      { text: "Check if the source is credible first", scores: { authority: 1 } },
      { text: "See if others you trust agree with the article", scores: { bandwagon: 1 } }
    ]
  },
  {
    id: 2,
    text: "When researching a big purchase, you tend to...",
    options: [
      { text: "Focus on reviews that match your initial impression", scores: { confirmation: 3 } },
      { text: "Seek out negative reviews specifically", scores: {} },
      { text: "Go with whatever has the most reviews", scores: { bandwagon: 2 } },
      { text: "Trust the expert recommendations", scores: { authority: 2 } }
    ]
  },
  {
    id: 3,
    text: "A friend shares a news story that seems too good/bad to be true. You...",
    options: [
      { text: "Share it if it aligns with your views", scores: { confirmation: 3 } },
      { text: "Fact-check it before reacting", scores: {} },
      { text: "Trust it if your friend seems confident", scores: { authority: 2 } },
      { text: "Wait to see if others share it too", scores: { bandwagon: 1 } }
    ]
  },
  // Anchoring Bias (Q4-Q5)
  {
    id: 4,
    text: "You're negotiating a salary. The employer mentions a number first. You...",
    options: [
      { text: "Use their number as a starting point for your counter", scores: { anchoring: 3 } },
      { text: "Ignore it and state your researched number", scores: {} },
      { text: "Assume they know the market better", scores: { authority: 2 } },
      { text: "Feel optimistic you can get more", scores: { optimism: 1 } }
    ]
  },
  {
    id: 5,
    text: "An AI gives you an estimate that seems reasonable. You...",
    options: [
      { text: "Use it as your baseline, adjusting slightly", scores: { anchoring: 3 } },
      { text: "Research independently to verify", scores: {} },
      { text: "Trust it - AI is good at this", scores: { authority: 2 } },
      { text: "Assume it's probably in the right ballpark", scores: { optimism: 1 } }
    ]
  },
  // Availability Heuristic (Q6-Q7)
  {
    id: 6,
    text: "After seeing news about a plane crash, you...",
    options: [
      { text: "Feel more nervous about your upcoming flight", scores: { availability: 3 } },
      { text: "Remember that flying is statistically very safe", scores: {} },
      { text: "Trust that experts wouldn't let unsafe planes fly", scores: { authority: 1 } },
      { text: "Figure it probably won't happen to you", scores: { optimism: 2 } }
    ]
  },
  {
    id: 7,
    text: "When estimating how common something is, you typically...",
    options: [
      { text: "Think of examples you can remember easily", scores: { availability: 3 } },
      { text: "Look up actual statistics", scores: {} },
      { text: "Go with what most people seem to think", scores: { bandwagon: 2 } },
      { text: "Trust your gut feeling", scores: { dunningKruger: 1 } }
    ]
  },
  // Dunning-Kruger Effect (Q8-Q10)
  {
    id: 8,
    text: "You've just learned the basics of a new skill. You feel...",
    options: [
      { text: "Pretty confident you understand it well", scores: { dunningKruger: 3 } },
      { text: "Aware there's much more to learn", scores: {} },
      { text: "Optimistic about mastering it quickly", scores: { optimism: 2 } },
      { text: "Ready to teach others the basics", scores: { dunningKruger: 2 } }
    ]
  },
  {
    id: 9,
    text: "When an expert disagrees with your opinion on their subject, you...",
    options: [
      { text: "Trust your own judgment - you've done research", scores: { dunningKruger: 3 } },
      { text: "Reconsider your position seriously", scores: {} },
      { text: "Defer to their expertise", scores: { authority: 2 } },
      { text: "Look for other experts who agree with you", scores: { confirmation: 2 } }
    ]
  },
  {
    id: 10,
    text: "How would you rate your driving compared to average?",
    options: [
      { text: "Above average", scores: { dunningKruger: 2, optimism: 1 } },
      { text: "About average", scores: {} },
      { text: "Below average", scores: {} },
      { text: "I don't drive / not sure", scores: {} }
    ]
  },
  // Sunk Cost Fallacy (Q11-Q12)
  {
    id: 11,
    text: "You're 2 hours into a bad movie. You...",
    options: [
      { text: "Keep watching - you've already invested the time", scores: { sunkCost: 3 } },
      { text: "Turn it off and do something better", scores: {} },
      { text: "Check if reviews say it gets better", scores: { authority: 1 } },
      { text: "Hope it improves by the end", scores: { optimism: 2 } }
    ]
  },
  {
    id: 12,
    text: "A project at work is clearly failing, but you've spent months on it. You...",
    options: [
      { text: "Push to continue - too much invested to stop", scores: { sunkCost: 3 } },
      { text: "Recommend cutting losses and pivoting", scores: {} },
      { text: "See what leadership wants to do", scores: { authority: 1 } },
      { text: "Stay optimistic it might turn around", scores: { optimism: 2 } }
    ]
  },
  // Authority Bias (Q13-Q14)
  {
    id: 13,
    text: "A famous CEO recommends a business practice you've never tried. You...",
    options: [
      { text: "Trust their judgment - they're successful for a reason", scores: { authority: 3 } },
      { text: "Research if it actually works for companies like yours", scores: {} },
      { text: "Try it if other companies are doing it too", scores: { bandwagon: 2 } },
      { text: "Figure it's worth a shot", scores: { optimism: 1 } }
    ]
  },
  {
    id: 14,
    text: "An AI assistant gives you advice that contradicts your instinct. You...",
    options: [
      { text: "Follow the AI - it has access to more information", scores: { authority: 3 } },
      { text: "Investigate further before deciding", scores: {} },
      { text: "Stick with your gut", scores: { dunningKruger: 1 } },
      { text: "Go with whichever option feels more optimistic", scores: { optimism: 1 } }
    ]
  },
  // Bandwagon Effect (Q15-Q16)
  {
    id: 15,
    text: "You're choosing a restaurant in an unfamiliar area. You pick...",
    options: [
      { text: "The one with the longest line outside", scores: { bandwagon: 3 } },
      { text: "Based on menu and reviews matching your preferences", scores: {} },
      { text: "The one recommended by a food critic", scores: { authority: 2 } },
      { text: "Whichever one you first noticed", scores: { anchoring: 1 } }
    ]
  },
  {
    id: 16,
    text: "A new app is going viral. Everyone seems to be using it. You...",
    options: [
      { text: "Download it immediately to see what the hype is about", scores: { bandwagon: 3 } },
      { text: "Wait to see if it's actually useful for you", scores: {} },
      { text: "Trust that many people can't be wrong", scores: { bandwagon: 1, authority: 1 } },
      { text: "Assume you'll probably like it too", scores: { optimism: 1 } }
    ]
  },
  // Optimism Bias (Q17-Q18)
  {
    id: 17,
    text: "When starting a new project, your estimates for completion time are usually...",
    options: [
      { text: "Too optimistic - things take longer than expected", scores: { optimism: 3 } },
      { text: "Pretty accurate", scores: {} },
      { text: "Padded with extra buffer time", scores: {} },
      { text: "Based on similar projects I've done", scores: { anchoring: 1 } }
    ]
  },
  {
    id: 18,
    text: "You hear about a common risk (health, financial, etc). You think...",
    options: [
      { text: "That probably won't happen to me", scores: { optimism: 3 } },
      { text: "I should take reasonable precautions", scores: {} },
      { text: "If experts say it's a risk, I should worry", scores: { authority: 2 } },
      { text: "I'll deal with it if it happens", scores: { optimism: 1 } }
    ]
  }
];

function generateSessionId() {
  return 'bingo_' + Math.random().toString(36).substring(2, 15);
}

type QuizPhase = 'intro' | 'questions' | 'results';

export default function BiasBingo() {
  const [phase, setPhase] = useState<QuizPhase>('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [scores, setScores] = useState<Record<BiasType, number>>({
    confirmation: 0, anchoring: 0, availability: 0, dunningKruger: 0,
    sunkCost: 0, authority: 0, bandwagon: 0, optimism: 0
  });
  const [results, setResults] = useState<{
    primary: BiasType;
    secondary: BiasType;
    typeName: string;
    allScores: Record<BiasType, number>;
  } | null>(null);
  const [startTime] = useState(Date.now());
  const [sessionId] = useState(generateSessionId);
  const [copied, setCopied] = useState(false);
  const [showReferralPrompt, setShowReferralPrompt] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fetchApi } = useApi();

  const source = searchParams.get('from') || 'direct';

  // Track start
  useEffect(() => {
    if (phase === 'questions' && currentQuestion === 0) {
      trackEvent('bingo_started', { source });
    }
  }, [phase]);

  async function trackEvent(eventType: string, data: Record<string, unknown> = {}) {
    try {
      await fetchApi('/api/bingo/event', {
        method: 'POST',
        body: JSON.stringify({ event_type: eventType, session_id: sessionId, ...data })
      });
    } catch (e) {
      console.error('Failed to track event:', e);
    }
  }

  function handleAnswer(optionIndex: number) {
    const question = QUESTIONS[currentQuestion];
    const option = question.options[optionIndex];

    // Update scores
    const newScores = { ...scores };
    Object.entries(option.scores).forEach(([bias, points]) => {
      newScores[bias as BiasType] += points;
    });
    setScores(newScores);

    // Record answer
    setAnswers([...answers, optionIndex]);

    // Next question or results
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResults(newScores);
    }
  }

  function calculateResults(finalScores: Record<BiasType, number>) {
    const sorted = Object.entries(finalScores).sort((a, b) => b[1] - a[1]) as [BiasType, number][];
    const primary = sorted[0][0];
    const secondary = sorted[1][0];
    const typeName = TYPE_MATRIX[primary]?.[secondary] || TYPE_MATRIX[secondary]?.[primary] || 'The Complex Thinker';

    const resultData = { primary, secondary, typeName, allScores: finalScores };
    setResults(resultData);
    setPhase('results');

    // Record completion
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    fetchApi('/api/bingo/complete', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        primary_bias: primary,
        secondary_bias: secondary,
        type_name: typeName,
        scores: finalScores,
        time_spent_seconds: timeSpent,
        source
      })
    }).catch(console.error);

    // Show referral prompt for logged-in users after a brief delay
    if (user) {
      setTimeout(() => setShowReferralPrompt(true), 2500);
    }
  }

  function handleShare() {
    trackEvent('bingo_shared', { type_name: results?.typeName });
    const shareUrl = `${window.location.origin}/bingo?shared=${results?.typeName?.replace(/\s+/g, '-')}`;
    navigator.clipboard.writeText(
      `I'm "${results?.typeName}" according to Bias Bingo! My top biases: ${BIAS_INFO[results!.primary].name} & ${BIAS_INFO[results!.secondary].name}. Take the quiz: ${shareUrl}`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function generateShareCard() {
    const canvas = canvasRef.current;
    if (!canvas || !results) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size for social sharing
    canvas.width = 1200;
    canvas.height = 630;

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 1200, 630);
    gradient.addColorStop(0, '#0f172a');
    gradient.addColorStop(1, '#1e293b');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1200, 630);

    // Type name
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 64px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`"${results.typeName}"`, 600, 200);

    // Subtitle
    ctx.fillStyle = '#94a3b8';
    ctx.font = '32px system-ui, sans-serif';
    ctx.fillText('My cognitive bias profile', 600, 260);

    // Top biases
    ctx.fillStyle = '#60a5fa';
    ctx.font = 'bold 28px system-ui, sans-serif';
    ctx.fillText(`${BIAS_INFO[results.primary].name} + ${BIAS_INFO[results.secondary].name}`, 600, 350);

    // Branding
    ctx.fillStyle = '#64748b';
    ctx.font = '24px system-ui, sans-serif';
    ctx.fillText('Bias Bingo • Northcoast AI Academy', 600, 550);
    ctx.fillText('northcoast.ai/bingo', 600, 590);

    // Download
    const link = document.createElement('a');
    link.download = 'bias-bingo-results.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  function handleCTA() {
    trackEvent('bingo_cta_clicked', { destination: user ? 'dashboard' : 'register' });
    navigate(user ? '/dashboard' : '/register?from=bingo');
  }

  // Get top 3 biases for results display
  const topBiases = results
    ? (Object.entries(results.allScores).sort((a, b) => b[1] - a[1]).slice(0, 3) as [BiasType, number][])
    : [];

  return (
    <div className="max-w-2xl mx-auto py-8">
      {/* Hidden canvas for share card generation */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Intro */}
      {phase === 'intro' && (
        <div className="text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/10 rounded-full text-purple-400 text-sm mb-4">
            <Brain className="w-4 h-4" />
            5-minute quiz
          </div>
          <h1 className="text-3xl font-bold text-white">Bias Bingo</h1>
          <p className="text-gray-400 text-lg max-w-md mx-auto">
            Discover which cognitive biases shape your thinking. Answer 18 quick scenarios to reveal your bias profile.
          </p>

          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-left space-y-3">
            <h3 className="font-semibold text-white">What you'll learn:</h3>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                Your top cognitive biases
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                Your unique "thinker type"
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                How these biases affect AI interactions
              </li>
            </ul>
          </div>

          <button
            onClick={() => setPhase('questions')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-purple-500 hover:bg-purple-400 text-white font-semibold rounded-lg transition-colors text-lg"
          >
            Start the Quiz
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Questions */}
      {phase === 'questions' && (
        <div className="space-y-6">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-400">
              <span>Question {currentQuestion + 1} of {QUESTIONS.length}</span>
              <span>{Math.round(((currentQuestion + 1) / QUESTIONS.length) * 100)}%</span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-500 transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / QUESTIONS.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            <p className="text-xl text-white mb-6">{QUESTIONS[currentQuestion].text}</p>

            <div className="space-y-3">
              {QUESTIONS[currentQuestion].options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  className="w-full text-left px-4 py-3 bg-gray-700/50 hover:bg-gray-700 border border-gray-600 hover:border-purple-500 rounded-lg text-gray-200 transition-all flex items-center gap-3 group"
                >
                  <span className="w-8 h-8 rounded-full bg-gray-600 group-hover:bg-purple-500 flex items-center justify-center text-sm font-medium transition-colors">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span>{option.text}</span>
                  <ChevronRight className="w-5 h-5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-purple-400" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {phase === 'results' && results && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-500/10 rounded-full text-green-400 text-sm mb-4">
              <Check className="w-4 h-4" />
              Quiz Complete
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">You're</h1>
            <h2 className="text-4xl font-bold text-purple-400 mb-4">"{results.typeName}"</h2>
          </div>

          {/* Top biases */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 space-y-4">
            <h3 className="font-semibold text-white text-lg">Your Top Biases</h3>
            {topBiases.map(([bias, score], idx) => (
              <div key={bias} className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full ${BIAS_INFO[bias].color} bg-opacity-20 flex items-center justify-center`}>
                  <span className="font-bold text-white">{idx + 1}</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-white">{BIAS_INFO[bias].name}</p>
                  <p className="text-sm text-gray-400">{BIAS_INFO[bias].description}</p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-gray-300">{score}</div>
                  <div className="text-xs text-gray-500">points</div>
                </div>
              </div>
            ))}
          </div>

          {/* Share buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleShare}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
            >
              {copied ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
              {copied ? 'Copied!' : 'Share Results'}
            </button>
            <button
              onClick={generateShareCard}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Card
            </button>
          </div>

          {/* CTA */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6 text-center">
            <p className="text-gray-200 mb-4">
              Understanding your biases is the first step. Learn to think more clearly with AI.
            </p>
            <button
              onClick={handleCTA}
              className="inline-flex items-center gap-2 px-8 py-4 bg-purple-500 hover:bg-purple-400 text-white font-semibold rounded-lg transition-colors"
            >
              {user ? 'Continue Learning' : 'Start Learning Free'}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Referral Prompt Modal */}
      {showReferralPrompt && (
        <ReferralPrompt
          trigger="bingo"
          onClose={() => setShowReferralPrompt(false)}
        />
      )}
    </div>
  );
}
